import React, { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "../../embedded-video.css";

const HlsPlayer = ({ streamUrl }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const [error, setError] = useState("");
  const [sessionKey, setSessionKey] = useState(null);

  // Generate one stable session key per page load
  const getRanKey = (size) => {
    const chars =
      "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ";
    let out = "";
    for (let i = 0; i < size; i++) {
      out += chars[Math.floor(Math.random() * chars.length)];
    }
    return out;
  };

  useEffect(() => {
    setSessionKey(getRanKey(32));
  }, []);

  useEffect(() => {
    if (!streamUrl || !sessionKey) return;

    const urlWithSession = `${streamUrl}?viewSession=${sessionKey}`;

    const checkStreamStatus = async () => {
      try {
        let response;

        try {
          response = await fetch(urlWithSession, {
            method: "HEAD",
            cache: "no-store",
          });
        } catch {
          response = await fetch(urlWithSession, {
            method: "GET",
            cache: "no-store",
          });
        }

        if (response) {
          if (response.status === 403) {
            setError("This content is not available in your region.");
            return;
          }
          if (response.status === 410) {
            setError("The event is no longer available.");
            return;
          }
          if (response.status === 204) {
            setError("Content will start shortly.");
            return;
          }
        }

        setError("");
        setupPlayer(urlWithSession);
      } catch (err) {
        console.warn(
          "[HlsPlayer] stream pre-check failed, continuing anyway",
          err,
        );
        setError("");
        setupPlayer(urlWithSession);
      }
    };

    checkStreamStatus();

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [streamUrl, sessionKey]);

  const setupPlayer = (srcUrl) => {
    if (!videoRef.current) return;

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    playerRef.current = videojs(videoRef.current, {
      autoplay: true,
      muted: true,
      preload: "auto",
      controls: true,
      fluid: true,
      playsinline: true,
      html5: {
        vhs: {
          enableLowInitialPlaylist: true,
          useNetworkInformationApi: true,
          limitRenditionByPlayerDimensions: true,
          fastQualityChange: true,
          overrideNative: !isIOS,
        },
        nativeAudioTracks: true,
        nativeVideoTracks: true,
      },
      sources: [
        {
          src: srcUrl,
          type: "application/x-mpegURL",
        },
      ],
    });
  };

  return (
    <div className="video-container">
      {error ? (
        <div
          className="flex items-center"
          style={{ width: "100%", height: "100%" }}
        >
          <div className="w-full text-center text-[18px]">{error}</div>
        </div>
      ) : (
        <video
          id="decimal_video"
          className="video-js"
          ref={videoRef}
          playsInline
          webkit-playsinline
          muted
          preload="auto"
          autoPlay
          controls
        />
      )}
    </div>
  );
};

export default HlsPlayer;
